import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson'
import type {
  ComparedDetectionModel,
  DetectionComparisonEntity,
  DetectionComparisonResponse,
  GeographicCoordinates,
} from '@/api/main/types.gen'

export const DEFAULT_MAX_DISTANCE_M = 10
export const DEFAULT_MAX_EDIT_DISTANCE = 1
export const MAX_EDIT_DISTANCE_LIMIT = 3
export const DEFAULT_SUPPRESSION_RADIUS_M = 2
export const MAX_SUPPRESSION_RADIUS_M = 10

/** Zoom level from which car footprints replace centre dots (same as the POC). */
export const FOOTPRINT_MIN_ZOOM = 17

export const CATEGORY_KEYS = ['matched', 'too_far', 'seen_once_first', 'seen_once_second'] as const
export type CategoryKey = (typeof CATEGORY_KEYS)[number]

export const CATEGORY_COLORS: Record<CategoryKey, string> = {
  matched: '#16a34a',
  too_far: '#f59e0b',
  seen_once_first: '#dc2626',
  seen_once_second: '#2563eb',
}

export const PAIR_LINE_COLOR = '#0f172a'
export const REJECTED_LINK_COLOR = '#475569'
export const SELECTED_OUTLINE_COLOR = '#0f172a'

const CAR_WIDTH_M = 1.9
const NOSE_FRACTION = 0.78

export type MatchingParams = {
  maxDistanceM: number
  maxEditDistance: number
  suppressionRadiusM: number
}

export const DEFAULT_MATCHING_PARAMS: MatchingParams = {
  maxDistanceM: DEFAULT_MAX_DISTANCE_M,
  maxEditDistance: DEFAULT_MAX_EDIT_DISTANCE,
  suppressionRadiusM: DEFAULT_SUPPRESSION_RADIUS_M,
}

export type EntityFilters = {
  categories: ReadonlySet<CategoryKey>
  drives: ReadonlySet<number>
  search: string
}

/** Seen-once entities are split by the drive they belong to so each drive gets its own colour. */
export const categoryKey = (entity: DetectionComparisonEntity, firstDriveId: number): CategoryKey => {
  if (entity.category === 'seen_once') {
    return entity.drive_ids[0] === firstDriveId ? 'seen_once_first' : 'seen_once_second'
  }
  return entity.category
}

export const entityColor = (entity: DetectionComparisonEntity, firstDriveId: number): string =>
  CATEGORY_COLORS[categoryKey(entity, firstDriveId)]

export const entityLabel = (entity: DetectionComparisonEntity): string => entity.plates.join(' / ')

export const formatMetres = (value: number | undefined, digits = 1): string =>
  value === undefined ? '-' : `${value.toFixed(digits)} m`

export const visibleMembers = (
  entity: DetectionComparisonEntity,
  drives: ReadonlySet<number>
): ComparedDetectionModel[] => entity.members.filter(member => drives.has(member.drive_id))

const memberMatchesSearch = (member: ComparedDetectionModel, needle: string): boolean =>
  (member.plate ?? '').toLowerCase().includes(needle) || String(member.detection_id).includes(needle)

export const filterEntities = (
  entities: DetectionComparisonEntity[],
  filters: EntityFilters,
  firstDriveId: number
): DetectionComparisonEntity[] => {
  const needle = filters.search.trim().toLowerCase()
  return entities.filter(entity => {
    if (!filters.categories.has(categoryKey(entity, firstDriveId))) return false
    const members = visibleMembers(entity, filters.drives)
    if (members.length === 0) return false
    return needle === '' || members.some(member => memberMatchesSearch(member, needle))
  })
}

export const countByCategory = (
  entities: DetectionComparisonEntity[],
  firstDriveId: number
): Record<CategoryKey, number> => {
  const counts: Record<CategoryKey, number> = { matched: 0, too_far: 0, seen_once_first: 0, seen_once_second: 0 }
  for (const entity of entities) counts[categoryKey(entity, firstDriveId)] += 1
  return counts
}

export const countByDrive = (entities: DetectionComparisonEntity[]): Record<number, number> => {
  const counts: Record<number, number> = {}
  for (const entity of entities) {
    for (const member of entity.members) counts[member.drive_id] = (counts[member.drive_id] ?? 0) + 1
  }
  return counts
}

type LngLat = [number, number]

const toLngLat = (point: GeographicCoordinates): LngLat => [point.longitude, point.latitude]

const metresPerDegree = (latitude: number): [number, number] => [
  111132.0,
  111320.0 * Math.cos((latitude * Math.PI) / 180) || 1e-6,
]

/**
 * Car-shaped polygon ring around the front/rear centre line, in [lng, lat] pairs.
 * Port of the POC `carShape`: a rectangle with the front corners pulled in to form a nose.
 */
export const carFootprint = (front: GeographicCoordinates, rear: GeographicCoordinates): LngLat[] => {
  const [mPerLat, mPerLon] = metresPerDegree((front.latitude + rear.latitude) / 2)
  const dx = (front.longitude - rear.longitude) * mPerLon
  const dy = (front.latitude - rear.latitude) * mPerLat
  const length = Math.hypot(dx, dy) || 1
  const [ux, uy] = [dx / length, dy / length]
  const [px, py] = [(-uy * CAR_WIDTH_M) / 2, (ux * CAR_WIDTH_M) / 2]

  const shift = (point: GeographicCoordinates, ox: number, oy: number): LngLat => [
    point.longitude + ox / mPerLon,
    point.latitude + oy / mPerLat,
  ]
  const shoulder: GeographicCoordinates = {
    longitude: rear.longitude + (front.longitude - rear.longitude) * NOSE_FRACTION,
    latitude: rear.latitude + (front.latitude - rear.latitude) * NOSE_FRACTION,
  }

  const ring: LngLat[] = [
    shift(rear, px, py),
    shift(shoulder, px, py),
    shift(front, 0, 0),
    shift(shoulder, -px, -py),
    shift(rear, -px, -py),
  ]
  ring.push(ring[0])
  return ring
}

export type MemberProperties = {
  entityId: string
  detectionId: number
  driveId: number
  plate: string
  color: string
}

export type ComparisonLayers = {
  footprints: FeatureCollection<Polygon, MemberProperties>
  dots: FeatureCollection<Point, MemberProperties>
  pairLines: FeatureCollection<LineString>
  rejectedLinks: FeatureCollection<LineString>
}

/**
 * GeoJSON for the map. Selection is not encoded here: the selected entity is rendered
 * through a separate overlay built from the same function, so the bulk sources stay stable.
 */
export const buildLayers = (
  entities: DetectionComparisonEntity[],
  options: {
    firstDriveId: number
    drives: ReadonlySet<number>
    showPairLines: boolean
    showRejectedLinks: boolean
  }
): ComparisonLayers => {
  const footprints: Feature<Polygon, MemberProperties>[] = []
  const dots: Feature<Point, MemberProperties>[] = []
  const pairLines: Feature<LineString>[] = []
  const rejectedLinks: Feature<LineString>[] = []

  for (const entity of entities) {
    const members = visibleMembers(entity, options.drives)
    const color = entityColor(entity, options.firstDriveId)

    for (const member of members) {
      const properties: MemberProperties = {
        entityId: entity.id,
        detectionId: member.detection_id,
        driveId: member.drive_id,
        plate: member.plate ?? '',
        color,
      }
      footprints.push({
        type: 'Feature',
        properties,
        geometry: { type: 'Polygon', coordinates: [carFootprint(member.front_center, member.rear_center)] },
      })
      dots.push({ type: 'Feature', properties, geometry: { type: 'Point', coordinates: toLngLat(member.center) } })
    }

    if (options.showPairLines && members.length === 2) {
      pairLines.push({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: members.map(member => toLngLat(member.center)) },
      })
    }

    if (options.showRejectedLinks && members.length > 0) {
      for (const candidate of entity.rejected) {
        rejectedLinks.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [toLngLat(members[0].center), toLngLat(candidate.center)] },
        })
      }
    }
  }

  return {
    footprints: { type: 'FeatureCollection', features: footprints },
    dots: { type: 'FeatureCollection', features: dots },
    pairLines: { type: 'FeatureCollection', features: pairLines },
    rejectedLinks: { type: 'FeatureCollection', features: rejectedLinks },
  }
}

export type LngLatBounds = [LngLat, LngLat]

export const entityBounds = (entity: DetectionComparisonEntity, drives: ReadonlySet<number>): LngLatBounds | null => {
  const points = visibleMembers(entity, drives).flatMap(member => [member.front_center, member.rear_center])
  if (points.length === 0) return null
  const longitudes = points.map(point => point.longitude)
  const latitudes = points.map(point => point.latitude)
  return [
    [Math.min(...longitudes), Math.min(...latitudes)],
    [Math.max(...longitudes), Math.max(...latitudes)],
  ]
}

export const responseBounds = (response: DetectionComparisonResponse): LngLatBounds | null =>
  response.bounds ? [toLngLat(response.bounds.south_west), toLngLat(response.bounds.north_east)] : null

export type ExportRow = {
  category: CategoryKey
  first_detection_id: number | null
  first_plate: string | null
  first_observed_at: string | null
  first_latitude: number | null
  first_longitude: number | null
  second_detection_id: number | null
  second_plate: string | null
  second_observed_at: string | null
  second_latitude: number | null
  second_longitude: number | null
  edit_distance: number | null
  separation_m: number | null
}

const EMPTY_HALF = {
  detection_id: null,
  plate: null,
  observed_at: null,
  latitude: null,
  longitude: null,
}

const half = (member: ComparedDetectionModel | undefined) =>
  member
    ? {
        detection_id: member.detection_id,
        plate: member.plate ?? null,
        observed_at: member.observed_at,
        latitude: member.center.latitude,
        longitude: member.center.longitude,
      }
    : EMPTY_HALF

const exportRow = (
  entity: DetectionComparisonEntity,
  first: ComparedDetectionModel | undefined,
  second: ComparedDetectionModel | undefined,
  firstDriveId: number
): ExportRow => {
  const a = half(first)
  const b = half(second)
  return {
    category: categoryKey(entity, firstDriveId),
    first_detection_id: a.detection_id,
    first_plate: a.plate,
    first_observed_at: a.observed_at,
    first_latitude: a.latitude,
    first_longitude: a.longitude,
    second_detection_id: b.detection_id,
    second_plate: b.plate,
    second_observed_at: b.observed_at,
    second_latitude: b.latitude,
    second_longitude: b.longitude,
    edit_distance: entity.edit_distance ?? null,
    separation_m: entity.separation_m ?? null,
  }
}

/**
 * One row per cross-drive match (both halves filled). Unmatched detections and the two
 * members of a same-drive match each get their own row with the other half empty.
 */
export const buildExportRows = (entities: DetectionComparisonEntity[], firstDriveId: number): ExportRow[] =>
  entities.flatMap(entity => {
    const inFirst = entity.members.filter(member => member.drive_id === firstDriveId)
    const inSecond = entity.members.filter(member => member.drive_id !== firstDriveId)
    if (entity.members.length === 2 && inFirst.length === 1 && inSecond.length === 1) {
      return [exportRow(entity, inFirst[0], inSecond[0], firstDriveId)]
    }
    return entity.members.map(member =>
      member.drive_id === firstDriveId
        ? exportRow(entity, member, undefined, firstDriveId)
        : exportRow(entity, undefined, member, firstDriveId)
    )
  })
